import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Calendar, ChevronDown, Tag, ArrowRight, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { GlowOrb } from "@/components/common/GlowOrb";
import Seo, { breadcrumbLd } from "@/components/seo/Seo";
import { useArticle } from "@/data/articleRepo";
import { getCaseStudyBySlug } from "@/data/caseStudies";
import { getArticleBySlug } from "@/data/articles";
import { trackClick, trackViewOnce } from "@/lib/analytics";
import { openEstimateWizard, buildConsultationHref } from "@/lib/cta";

const SERVICE_LABELS = {
  "ai-development": "AI Development",
  "ai-agent-development": "AI Agent Development",
  "custom-software-development": "Custom Software Development",
  "saas-development": "SaaS Development",
  "crm-development": "CRM Development",
  "mobile-app-development": "Mobile App Development",
  "web-application-development": "Web Application Development",
  "edtech-development": "EdTech Development",
  "mvp-development": "MVP Development",
  "software-modernisation": "Software Modernisation",
};

/** Minimal, safe markdown converter — supports **bold**, bullet lines
 *  starting with "- ", and inline backticks. Used only inside structured
 *  section bodies so XSS surface is small. */
function inlineMd(s) {
  if (!s) return "";
  return s
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/`([^`]+)`/g, "<code>$1</code>");
}

export default function BlogPost() {
  const { slug } = useParams();
  const { data: post, loading, error } = useArticle(slug);

  useEffect(() => {
    if (!post) return;
    trackViewOnce("article_view", {
      article_slug: post.slug,
      article_title: post.title,
      content_cluster: post.contentCluster || "unclustered",
      primary_service: post.primaryService || "none",
      source: post.source,
    });
  }, [post]);

  if (error && !post) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-40 text-center" data-testid="blog-post-not-found">
        <Seo title="Article not found" description="" noindex />
        <h1 className="font-heading font-bold text-3xl">Article not found</h1>
        <Link to="/blog" className="mt-4 inline-block text-primary">Back to blog</Link>
      </div>
    );
  }
  if (!post) {
    return <div className="max-w-3xl mx-auto px-6 py-40"><div className="h-96 glass rounded-3xl animate-pulse" /></div>;
  }

  const noindex = !!post.seo?.noindex;
  const canonical = post.seo?.canonicalOverride || `https://www.cloudlabweb.com/blog/${post.slug}`;
  const faqs = post.faqs || [];
  const ap = post.answerProfile || {};
  const isCodeManaged = post.source === "code";

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    image: post.heroImage ? [post.heroImage] : undefined,
    datePublished: post.publishedAt,
    dateModified: post.updatedAt || post.publishedAt,
    author: {
      "@type": post.author?.role ? "Organization" : "Organization",
      name: post.author?.name || "CloudLab Technologies",
      url: post.author?.url || "https://www.cloudlabweb.com/",
    },
    publisher: {
      "@type": "Organization",
      name: "CloudLab Technologies",
      logo: { "@type": "ImageObject", url: "https://www.cloudlabweb.com/logo.png" },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
    articleSection: post.category,
  };
  const breadcrumb = breadcrumbLd([
    { name: "Home", path: "/" },
    { name: "Blog", path: "/blog" },
    { name: post.title, path: `/blog/${post.slug}` },
  ]);
  const faqLd = faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  } : null;

  const relatedCases = (post.relatedCaseStudies || [])
    .map((s) => getCaseStudyBySlug(s))
    .filter((r) => r && r.status === "published");

  // Phase 4B–E — related articles (code-managed only; cross-cluster links).
  const relatedArticles = (post.relatedArticles || [])
    .map((s) => getArticleBySlug(s))
    .filter((a) => a && a.status === "published")
    .slice(0, 3);

  const openEstimate = (location = "final") => {
    trackClick("cta_clicked", {
      cta_type: post.primaryCTA?.kind || "get_project_estimate",
      source_type: "article",
      cta_location: location,
      article_slug: post.slug,
      service_slug: post.primaryService || undefined,
      content_cluster: post.contentCluster || undefined,
    });
    openEstimateWizard({
      serviceSlug: post.primaryService || null,
      sourceType: "article",
      ctaLocation: location,
    });
  };

  const consultationHref = buildConsultationHref({
    serviceSlug: post.primaryService || null,
  });

  return (
    <article data-testid="blog-post-page">
      <Seo
        title={post.seo?.title || post.title}
        description={post.seo?.description || post.excerpt}
        image={post.seo?.ogImage || post.heroImage}
        type="article"
        noindex={noindex}
        canonical={canonical}
        jsonLd={[articleLd, breadcrumb, ...(faqLd ? [faqLd] : [])]}
      />

      <section className="relative overflow-hidden py-16 md:py-20">
        <GlowOrb className="top-0 left-0" color="primary" size={500} />
        <div className="relative max-w-4xl mx-auto px-6">
          <nav aria-label="Breadcrumb" className="text-[11px] font-mono uppercase tracking-[0.25em] text-muted-foreground">
            <Link to="/" className="hover:text-primary transition-colors">Home</Link>
            <span className="mx-2 opacity-50">/</span>
            <Link to="/blog" className="hover:text-primary transition-colors">Blog</Link>
            <span className="mx-2 opacity-50">/</span>
            <span className="text-foreground line-clamp-1">{post.title}</span>
          </nav>
          <div className="mt-6 flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.15em] text-primary">
            <span>{post.category}</span>
            {post.publishedAt && <>
              <span>·</span>
              <span className="text-muted-foreground inline-flex items-center gap-1">
                <Calendar className="w-3 h-3" /> {new Date(post.publishedAt).toLocaleDateString()}
              </span>
            </>}
          </div>
          <h1 className="mt-4 font-heading font-black tracking-tight text-4xl md:text-5xl lg:text-6xl leading-[1.05]" data-testid="blog-post-h1">
            {post.title}
          </h1>
          <p className="mt-6 text-lg text-muted-foreground leading-relaxed">{post.excerpt}</p>
          {post.author?.name && (
            <div className="mt-6 text-xs text-muted-foreground">
              By <span className="text-foreground font-medium">{post.author.name}</span>
              {post.author.role && <span> · {post.author.role}</span>}
            </div>
          )}
        </div>
      </section>

      {post.heroImage && (
        <div className="max-w-5xl mx-auto px-6">
          <div className="aspect-[16/9] rounded-3xl overflow-hidden bg-gradient-to-br from-primary/20 via-secondary/10 to-primary/5">
            <img
              src={post.heroImage} alt={post.title}
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          </div>
        </div>
      )}

      {/* Answer-oriented summary (GEO / ARO) — only when structured data present */}
      {isCodeManaged && (ap.whatThisIs || ap.whoItIsFor || ap.problemSolved || ap.howItWorks || (ap.keyCapabilities?.length) || (ap.importantFacts?.length)) && (
        <section className="relative max-w-5xl mx-auto px-6 py-10" data-testid="blog-post-answer-profile">
          <div className="rounded-3xl border border-border bg-card p-6 md:p-8">
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-primary">Quick answer</div>
            <div className="mt-5 grid md:grid-cols-2 gap-x-10 gap-y-5">
              {ap.whatThisIs && <APField label="What this is" value={ap.whatThisIs} />}
              {ap.whoItIsFor && <APField label="Who it is for" value={ap.whoItIsFor} />}
              {ap.problemSolved && <APField label="Problem solved" value={ap.problemSolved} />}
              {ap.howItWorks && <APField label="How it works" value={ap.howItWorks} />}
            </div>
            {ap.keyCapabilities?.length > 0 && (
              <div className="mt-6">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">Key capabilities</div>
                <ul className="mt-3 grid sm:grid-cols-2 gap-2.5">
                  {ap.keyCapabilities.map((c, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm leading-relaxed">
                      <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {ap.importantFacts?.length > 0 && (
              <div className="mt-6">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">Verified facts</div>
                <ul className="mt-3 space-y-1.5 text-sm leading-relaxed text-foreground/85">
                  {ap.importantFacts.map((c, i) => (<li key={i} className="pl-4 border-l-2 border-primary/40">{c}</li>))}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ---- Code-managed strategic content: structured sections ---- */}
      {isCodeManaged && post.sections?.length > 0 && (
        <section className="py-10 md:py-14" data-testid="blog-post-sections">
          <div className="max-w-3xl mx-auto px-6 space-y-10">
            {post.sections.map((s) => (
              <section key={s.id} id={s.id}>
                <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight leading-[1.1]">{s.heading}</h2>
                <div className="mt-5 space-y-5 text-base md:text-lg leading-relaxed text-foreground/85">
                  {s.body.map((para, i) => (
                    para.startsWith("- ")
                      ? null
                      : <p key={i} dangerouslySetInnerHTML={{ __html: inlineMd(para) }} />
                  ))}
                  {/* collapse consecutive bullet lines into a <ul> */}
                  {s.body.some((p) => p.startsWith("- ")) && (
                    <ul className="list-disc pl-6 space-y-2">
                      {s.body.filter((p) => p.startsWith("- ")).map((p, i) => (
                        <li key={i} dangerouslySetInnerHTML={{ __html: inlineMd(p.slice(2)) }} />
                      ))}
                    </ul>
                  )}
                </div>
              </section>
            ))}
          </div>
        </section>
      )}

      {/* ---- DB-managed legacy markdown (AI-generated blogs) ---- */}
      {!isCodeManaged && post.legacyMarkdown && (
        <section className="py-16">
          <div className="max-w-3xl mx-auto px-6 prose-container">
            <div
              className="prose prose-lg max-w-none dark:prose-invert prose-headings:font-heading prose-headings:tracking-tight prose-h2:mt-10 prose-h2:mb-4 prose-h3:mt-8 prose-a:text-primary prose-strong:text-foreground prose-blockquote:border-primary prose-blockquote:italic prose-li:my-1"
              dangerouslySetInnerHTML={{ __html: legacyMdToHtml(post.legacyMarkdown) }}
              data-testid="blog-post-content"
            />
            {post.tags?.length > 0 && (
              <div className="mt-12 flex flex-wrap gap-2 items-center">
                <Tag className="w-4 h-4 text-muted-foreground" />
                {post.tags.map((t) => (<span key={t} className="px-3 py-1 rounded-full bg-foreground/5 border border-border text-xs font-mono">{t}</span>))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ---- FAQs ---- */}
      {faqs.length > 0 && (
        <section className="py-10 md:py-14" data-testid="blog-post-faqs">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="font-heading font-bold text-3xl mb-6">Frequently asked</h2>
            <div className="space-y-3">
              {faqs.map((f, i) => (
                <details key={i} className="group glass rounded-2xl px-5 py-4">
                  <summary className="flex items-center justify-between cursor-pointer font-heading font-semibold text-base list-none">
                    <span>{f.q}</span>
                    <ChevronDown className="w-4 h-4 group-open:rotate-180 transition-transform" />
                  </summary>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ---- Related services (code-managed only) ---- */}
      {isCodeManaged && post.relatedServices?.length > 0 && (
        <section className="py-10 md:py-14" data-testid="blog-post-related-services">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-primary">Related services</div>
            <h2 className="mt-2 font-heading font-bold text-2xl md:text-3xl tracking-tight">Explore the capabilities behind this article.</h2>
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {post.relatedServices.map((s) => (
                <Link key={s} to={`/services/${s}`}
                  onClick={() => trackClick("cta_clicked", { cta_type: "article_related_service_click", source_type: "article", article_slug: post.slug, service_slug: s })}
                  className="group rounded-2xl border border-border bg-card p-5 hover:border-primary/50 transition-colors"
                  data-testid={`blog-related-service-${s}`}
                >
                  <div className="font-heading font-bold text-base leading-tight group-hover:text-primary transition-colors">{SERVICE_LABELS[s] || s}</div>
                  <div className="mt-3 inline-flex items-center gap-1.5 text-sm text-primary">Read more <ArrowRight className="w-3.5 h-3.5" /></div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ---- Related case studies ---- */}
      {relatedCases.length > 0 && (
        <section className="py-10 md:py-14" data-testid="blog-post-related-cases">
          <div className="max-w-6xl mx-auto px-6">
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground">Evidence</div>
                <div className="mt-1 font-heading font-bold text-2xl md:text-3xl">Related case studies</div>
              </div>
              <Link to="/case-studies" className="text-xs font-mono uppercase tracking-widest text-primary hover:underline inline-flex items-center gap-1">
                All case studies <ArrowUpRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              {relatedCases.map((cs) => (
                <Link key={cs.slug} to={`/case-studies/${cs.slug}`}
                  onClick={() => trackClick("cta_clicked", { cta_type: "article_case_study_click", source_type: "article", article_slug: post.slug, case_study_slug: cs.slug })}
                  className="group block rounded-3xl overflow-hidden border border-border bg-card hover:border-primary/40 transition-colors"
                  data-testid={`blog-related-case-${cs.slug}`}
                >
                  <div className="aspect-[16/9] overflow-hidden bg-muted/40">
                    {cs.heroImage && <img src={cs.heroImage} alt={cs.projectName} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
                  </div>
                  <div className="p-5">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-primary">{cs.industry}</div>
                    <div className="mt-1.5 font-heading font-bold text-lg leading-tight">{cs.projectName}</div>
                    <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{cs.shortDescription}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ---- Related articles (code-managed only, cluster cross-links) ---- */}
      {isCodeManaged && relatedArticles.length > 0 && (
        <section className="py-10 md:py-14" data-testid="blog-post-related-articles">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-primary">Keep reading</div>
            <h2 className="mt-2 font-heading font-bold text-2xl md:text-3xl tracking-tight">More from this cluster.</h2>
            <div className="mt-6 grid md:grid-cols-3 gap-4">
              {relatedArticles.map((a) => (
                <Link key={a.slug} to={`/blog/${a.slug}`}
                  onClick={() => trackClick("cta_clicked", { cta_type: "article_related_article_click", source_type: "article", article_slug: post.slug, target_article_slug: a.slug })}
                  className="group block rounded-2xl border border-border bg-card overflow-hidden hover:border-primary/40 transition-colors"
                  data-testid={`blog-related-article-${a.slug}`}
                >
                  {a.heroImage && (
                    <div className="aspect-[16/9] overflow-hidden bg-muted/40">
                      <img src={a.heroImage} alt={a.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    </div>
                  )}
                  <div className="p-5">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-primary">{a.category}</div>
                    <div className="mt-1.5 font-heading font-bold text-base leading-tight line-clamp-2 group-hover:text-primary transition-colors">{a.title}</div>
                    <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{a.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ---- Final CTA (code-managed only — DB blogs keep their own flow) ---- */}
      {isCodeManaged && (
        <section className="py-14 md:py-20" data-testid="blog-post-final-cta">
          <div className="max-w-6xl mx-auto px-6">
            <div className="relative rounded-[32px] overflow-hidden p-8 md:p-14 bg-foreground text-background shadow-2xl">
              <div aria-hidden="true" className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full opacity-40 blur-3xl pointer-events-none bg-primary" />
              <div className="relative grid lg:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="text-xs font-mono uppercase tracking-[0.25em] text-primary">Thinking about building this?</div>
                  <h2 className="mt-3 font-heading font-bold text-3xl md:text-4xl lg:text-5xl leading-[1.02] tracking-tight">
                    Share a rough scope — we&apos;ll come back within one working day.
                  </h2>
                </div>
                <div className="flex flex-wrap gap-3 lg:justify-end">
                  <button onClick={() => openEstimate("final")} data-testid="blog-post-estimate-cta"
                    className="group inline-flex items-center gap-3 rounded-full pl-6 pr-2 py-2 text-sm font-medium bg-primary text-primary-foreground">
                    <span>{post.primaryCTA?.label || "Get a Project Estimate"}</span>
                    <span className="w-8 h-8 rounded-full bg-background text-primary flex items-center justify-center transition-transform group-hover:translate-x-0.5">
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </button>
                  <Link to={consultationHref}
                    onClick={() => trackClick("cta_clicked", { cta_type: "book_consultation", source_type: "article", cta_location: "final", article_slug: post.slug, service_slug: post.primaryService || undefined })}
                    data-testid="blog-post-consultation-cta"
                    className="rounded-full px-5 py-3 border border-background/25 text-sm inline-flex items-center gap-2 hover:bg-background/5 text-background"
                  >
                    <Calendar className="w-4 h-4" /> {post.secondaryCTA?.label || "Book a 15-Minute Consultation"}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </article>
  );
}

function APField({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm md:text-base leading-relaxed text-foreground/90">{value}</div>
    </div>
  );
}

/** Minimal legacy markdown → HTML. Only used for DB-backed AI blogs. */
function legacyMdToHtml(md) {
  if (!md) return "";
  let html = md
    .replace(/^### (.*$)/gim, "<h3>$1</h3>")
    .replace(/^## (.*$)/gim, "<h2>$1</h2>")
    .replace(/^# (.*$)/gim, "<h1>$1</h1>")
    .replace(/\*\*(.*?)\*\*/gim, "<strong>$1</strong>")
    .replace(/\*(.*?)\*/gim, "<em>$1</em>")
    .replace(/^> (.*$)/gim, "<blockquote>$1</blockquote>")
    .replace(/^\- (.*$)/gim, "<li>$1</li>")
    .replace(/(<li>.*<\/li>)/gims, "<ul>$1</ul>");
  html = html
    .split(/\n\n+/)
    .map((chunk) => {
      const t = chunk.trim();
      if (!t) return "";
      if (/^<h\d|<ul|<blockquote|<pre|<img/.test(t)) return t;
      return `<p>${t.replace(/\n/g, "<br/>")}</p>`;
    })
    .join("\n");
  return html;
}
